import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { DataTableDirective } from 'angular-datatables';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, finalize, map, of, skip, switchMap, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { RecenziiService } from './recenzii.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { IRecenziiSectiuni } from './recenzii.interface';
import { DatePipe } from '@angular/common';
import Swal from 'sweetalert2';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-recenzii',
    templateUrl: './recenzii.component.html',
    styleUrls: ['./recenzii.component.scss']
})
export class RecenziiComponent {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    searchedCitizens: Generic[] = [];
    citizenChangesSubscription = new Subscription();
    recenziiSectiuni: IRecenziiSectiuni[] = [];

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';
    statusCerere!: Generic[];

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);
        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    formatDate(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    // Date popover filtering

    dateFilteringOptions: string[] = [
        'Adăugare',
    ];

    dateFilterOption: string = 'Adăugare';

    // Values for filtering
    defaultParameter: { name: string; id: string } = {
        name: 'Toate',
        id: '-1',
    };

    tipActiuneOptions: { name: string; id: string }[] = [
        { name: 'Toate', id: '-1' },
        { name: 'Sesizare', id: '1' },
        { name: 'Plată taxe și impozite', id: '2' },
        { name: 'Parcări: Depunere solicitare', id: '3' },
    ];
    notaOptions: { name: string; id: string }[] = [
        { name: 'Toate', id: '-1' },
        { name: '1', id: '1' },
        { name: '2', id: '2' },
        { name: '3', id: '3' },
        { name: '4', id: '4' },
        { name: '5', id: '5' },
    ];
    statusOptions: { name: string; id: string }[] = [
        { name: 'Toate', id: '-1' },
        { name: 'Da', id: '1' },
        { name: 'Actualizări/șterse', id: '2' },
    ];
    spamOptions: { name: string; id: string }[] = [
        { name: 'Toate', id: '-1' },
        { name: 'Da', id: '1' },
        { name: 'Nu', id: '2' },
    ];

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    toggleCard() {
        this.showCard = !this.showCard;
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    searchParamsForm!: FormGroup;

    //Form Initial Values

    initialFormValues = {
        action_type: '-1',
        nota: '-1',
        status: '1',
        cetatean_id: '',
        cetatean_name: '',
        status_spam: '-1',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            action_type: new FormControl('-1'),
            nota: new FormControl('-1'),
            status: new FormControl('1'), // active true by default
            cetatean_id: new FormControl(''),
            cetatean_name: new FormControl(''),
            status_spam: new FormControl('-1'),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.getRecenziiSectiuni();
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.showCard = false;
        this.getRecenziiSectiuni();
        this.rerenderDT(true);
    }

    refreshPage() {
        this.getRecenziiSectiuni();
        this.rerenderDT(false);
    }

    dtLink =
        environment.interop.basePath +
        environment.interop.platform.nomenclatoare.recenzii.getRecenzii;

    constructor(
        private http: HttpClient,
        private recenziiService: RecenziiService,
        public permissionsService: PermissionsService,
        private datePipe: DatePipe,
        private commonService: CommonService,
    ) {
        this.tomorrow.setDate(this.today.getDate() + 1);
    }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
        this.getRecenziiSectiuni();
        this.renderCitizenSearch();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }


    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
        this.citizenChangesSubscription.unsubscribe();
    }

    renderDT() {
        this.dtOptions = {
            order: [[1, 'desc']],
            responsive: true,
            serverSide: true,
            searching: false,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;
                dataTablesParameters.filters = this.searchParamsForm.value;

                let date_from = this.formatDate(this.selectedStartDate);
                let date_to = this.formatDate(this.selectedEndDate);

                dataTablesParameters.tip_date = this.dateFilterOption;
                dataTablesParameters.date_from = date_from;
                dataTablesParameters.date_to = date_to;
                dataTablesParameters.filters.tip_date = this.dateFilterOption;
                dataTablesParameters.filters.date_from = date_from;
                dataTablesParameters.filters.date_to = date_to;
                // dataTablesParameters.notificationParameter =
                //     this.notificationParameter.id;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columnDefs: [
                { width: '3%', targets: 0 },
                { width: '15%', targets: 1 },
                { width: '20%', targets: 2 },
                { width: '5%', targets: 3 },
                { width: '20%', targets: 4 },
                { width: '15%', targets: 5 },
                { width: '12%', targets: 6 },
                { width: '10%', targets: 7 },
            ],

            columns: [
                {
                    title: `ID`,
                    data: 'id',
                    sortable: true,
                    render: (data: any, type: any, row: any) => {
                        return `${row.id}`;
                    },
                },
                {
                    title: 'Cetățean',
                    data: 'denumire_cetatean',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `
							<span>${row.denumire_cetatean}</span>
						`;
                    },
                },
                {
                    title: 'Tipul acțiunii',
                    data: 'action_type_denumire',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        let label = '';
                        let flagIcon = '<i class="fa-solid fa-flag" style="color: white;"></i> ';
                        if (row.flag === true) {
                            label = `<span style="border-radius:10px; padding:4px 8px; color: white; background: red">${flagIcon}Spam</span>`
                        }
                        return `
                            <span>${row.action_type_denumire}</span>
                            <br/>
                            <br/>${label}
                        `;
                    },
                },
                {
                    title: 'Notă',
                    data: 'note',
                    sortable: true,
                    render: (data: any, type: any, row: any) => {
                        return `
                            <span>${row.note}</span>
                        `;
                    },
                },
                {
                    title: 'Notă observații',
                    data: 'note_obs',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `
                        <span>${row.note_obs}</span><br/>
                    `;
                    },
                },
                {
                    title: 'Acțiune observații',
                    data: 'action_obs',
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `
                            <span>${row.action_obs}</span>
                        `;
                    },
                },
                {
                    title: 'Data',
                    data: 'created_at',
                    sortable: true,
                    render: (data: any, type: any, row: any) => {
                        return `
                        <span>${this.datePipe.transform(row.created_at, 'dd.MM.yyyy HH:mm')}</span>
                        `;
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    render: () => {
                        return `
                        <div class="d-flex align-center justify-content-center">
                            <button class="action-btn remove_spam ms-1 action-danger view">
                                <img class="btn-img" src="../../../../assets/icons/ban-solid.svg" alt="remove_spam"/>
                            </button>
                            <button class="action-btn mark_spam ms-1 action-success view">
                                <img class="btn-img" src="../../../../assets/icons/circle-check-regular.svg" alt="mark_spam"/>
                            </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any, index: number) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const markSpamIcon = lastTd?.querySelector(
                    '.remove_spam'
                ) as HTMLElement;
                const removeSpamIcon = lastTd?.querySelector(
                    '.mark_spam'
                ) as HTMLElement;


                if (markSpamIcon) {
                    const { id, flag } = data;
                    // if (!this.permissionsService.hasPermission('auth-module.settings.directii.update-status')) {
                    //     updateStatusActiveIcon.style.display = 'none';
                    // }
                    if (flag === true) {
                        markSpamIcon.style.display = 'none'
                    }
                    markSpamIcon.addEventListener('click', () => {
                        this.markSpamAction(id);
                    });
                }

                if (removeSpamIcon) {
                    const { id, flag } = data;
                    // if (!this.permissionsService.hasPermission('auth-module.settings.directii.update-status')) {
                    //     updateStatusActiveIcon.style.display = 'none';
                    // }
                    if (flag !== true) {
                        removeSpamIcon.style.display = 'none'
                    }
                    removeSpamIcon.addEventListener('click', () => {
                        this.removeSpam(id);
                    });
                }

                return row;
            },
        };

    }

    rerenderDT(paginate = false): void {
        // notifications - skip rerender when dt is not loaded. Rerender is called by notificationService.currentNotification subscription.
        if (!this.dtElement || !this.dtElement.dtInstance) return;

        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });
    }

    renderCitizenSearch() {
        this.citizenChangesSubscription = this.searchParamsForm
            .controls['cetatean_name'].valueChanges.pipe(
                skip(1), // Ignore the first emitted value
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((citizenValue) => {
                    // Reset Citizen ID before search
                    this.searchParamsForm.controls['cetatean_id'].setValue('');

                    this.isFilterLoading = true;
                    return this.commonService.searchCitizen(citizenValue);
                }),
                tap((data: Generic[]) => {
                    this.isFilterLoading = false;
                    this.searchedCitizens = data;
                }),
                catchError(() => of([]))
            )
            .subscribe();
    }

    onCitizenSelected(option: { id: number; name: string }) {
        // Unsubscribe from valueChanges to avoid an unnecessary call to the BE when setting a value
        this.citizenChangesSubscription.unsubscribe();
        console.log(option, 'option');
        this.searchParamsForm.controls['cetatean_name'].setValue(option.name);
        this.searchParamsForm.controls['cetatean_id'].setValue(option.id);

        // Resubscribe to valueChanges after setting the value
        this.renderCitizenSearch();
    }

    getRecenziiSectiuni() {
        const payload = {
            ...this.searchParamsForm.value,
            date_from: this.formatDate(this.selectedStartDate),
            date_to: this.formatDate(this.selectedEndDate),
            tip_date: this.dateFilterOption,
        };

        this.recenziiService.getRecenziiSectiuni(payload).pipe(
            map((results) => results.data),
        ).subscribe({
            next: (results) => {
                this.recenziiSectiuni = results;
                console.log(this.recenziiSectiuni);
            },
            error: (error) => {
                console.error('Error loading recenzii sectiuni', error);
            },
        })
    }

    markSpamAction(id: number) {
        Swal.fire({
            title: 'Sunteți sigur?',
            text: 'O să marcați recenzia ca spam!',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Confirm!',
            cancelButtonText: 'Anulați',
            input: 'text', // Enable text input
            inputPlaceholder: 'Completați motivul pentru care recenzia este spam', // Placeholder for input
            inputValidator: (value: any) => {
                if (!value) {
                    return 'Este necesar completarea un motiv!'; // Validation message if input is empty
                }
                return null; // Return null if input is valid
            },
            showLoaderOnConfirm: true,
            preConfirm: (inputValue) => {
                return this.recenziiService
                    .markSpamRecenzie(
                        id,
                        inputValue,
                    ) // Assuming 'this.http' is your HttpClient
                    .toPromise()
                    .then((response) => {
                        return response; // You might want to do something with the input value and response here
                    })
                    .catch((err) => {
                        Swal.showValidationMessage(
                            `Request failed: ${err.error.errors.message[0]}`
                        );
                    });
            },
            allowOutsideClick: () => !Swal.isLoading(),
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Succes!',
                    text: 'Recenzia a fost actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: 'Închide',
                }).then(() => {
                    this.getRecenziiSectiuni();
                    this.rerenderDT();
                });
            }
        });
    }

    removeSpam(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'O sa eliminați marcajul de spam!',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.removeSpamAction(id);
            }
        });
    }

    removeSpamAction(id: number) {
        this.isLoading = true;
        this.recenziiService.removeSpamRecenzie(id).subscribe(
            () => {
                this.getRecenziiSectiuni();
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Recenzie actualizată cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            },
            (err) => {
                let msg;
                try {
                    // try to catch the message from server
                    msg = err.error.errors.message[0];
                } catch (error) {
                    // controll output message
                    msg = helper.dialogConfig.generalMessages.error;
                }
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.errorAtentie,
                    text: msg,
                    icon: 'error',
                    confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                    showCancelButton: true,
                    cancelButtonText: helper.dialogConfig.buttonLabels.close,
                    reverseButtons: true,
                    allowOutsideClick: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.removeSpamAction(id);
                    }
                });
            }
        );
    }

    getMedie(nota: string): number {
        const notaNumber = parseFloat(nota);
        const percentage = (notaNumber / 5) * 100;
        return percentage;
    }

    getPercentage(nota: string): number {
        return (parseFloat(nota) / 5) * 100;
    }

}
